import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from '../components/ScrollX';
import { supabase } from '../supabase';
import { clickUnlessSelecting } from '../utils/clickGuard';
import AddListModal from '../components/AddListModal';
import AddOptionModal from '../components/AddOptionModal';
import EmployeeTable from '../components/EmployeeTable';
import AssignmentGrid from '../components/AssignmentGrid';
import DocFoldersTable from '../components/DocFoldersTable';
import NoticeTypesTable from '../components/NoticeTypesTable';
import ClientGroupsTable from '../components/ClientGroupsTable';
import LevelNamesTable from '../components/LevelNamesTable';

// Table Management — generic admin-editable pick lists (lookup_lists → lookup_options).
// Lists level → click a list → its options (add/edit/retire). Powers future dropdowns with zero code.
export default function TableManagementPage({ darkMode }) {
  const isDark = darkMode;
  const c = isDark ? {
    card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)',
    input: 'var(--ink)', inputBorder: '#434C68', th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.04)',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)',
    input: '#ffffff', inputBorder: '#c8ccd8', th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
  };
  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    th: { padding: '9px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', fontSize: '13px', fontWeight: '500', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}` },
    badge: (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600' }),
    btn: { padding: '8px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    btnGhost: { padding: '6px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    back: { cursor: 'pointer', color: 'var(--red)', fontSize: '13px', fontWeight: '600' },
  };

  // ── State ──────────────────────────────────────────────────────
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionCountByList, setOptionCountByList] = useState({}); // list_id -> # options
  const [showInactive, setShowInactive] = useState(false);
  const [drill, setDrill] = useState(null);          // selected pick list
  const [refTable, setRefTable] = useState(null);    // selected structured reference table (e.g. 'employees')
  const [options, setOptions] = useState([]);        // options for drill
  const [editList, setEditList] = useState(null);
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const [editOption, setEditOption] = useState(null);

  // ── Loads ──────────────────────────────────────────────────────
  // One bulk query of options (list_id only), grouped client-side → count map. No N+1 per row.
  const refreshOptionCounts = useCallback(async () => {
    const { data } = await supabase.from('lookup_options').select('list_id');
    const m = {};
    (data || []).forEach(r => { m[r.list_id] = (m[r.list_id] || 0) + 1; });
    setOptionCountByList(m);
  }, []);

  useEffect(() => {
    supabase.from('lookup_lists').select('*').order('name').then(({ data }) => {
      setLists(data || []);
      setLoading(false);
    });
    refreshOptionCounts();
  }, [refreshOptionCounts]);

  const loadOptions = useCallback(async (listId) => {
    const { data } = await supabase.from('lookup_options').select('*').eq('list_id', listId)
      .order('sort_order', { ascending: true }).order('label', { ascending: true });
    setOptions(data || []);
  }, []);

  const loadDrill = useCallback((list) => {
    setDrill(list);
    setOptions([]);
    loadOptions(list.id);
  }, [loadOptions]);

  const refreshLists = useCallback(() =>
    supabase.from('lookup_lists').select('*').order('name').then(({ data }) => setLists(data || [])), []);

  async function toggleListActive() {
    const next = !drill.active;
    if (!window.confirm(next ? `Reactivate "${drill.name}"?` : `Retire "${drill.name}"? It'll be hidden unless "Show inactive" is on.`)) return;
    await supabase.from('lookup_lists').update({ active: next }).eq('id', drill.id);
    const updated = { ...drill, active: next };
    setDrill(updated);
    setLists(prev => prev.map(l => l.id === drill.id ? updated : l));
  }

  async function toggleOptionActive(opt) {
    const next = !opt.active;
    await supabase.from('lookup_options').update({ active: next }).eq('id', opt.id);
    setOptions(prev => prev.map(o => o.id === opt.id ? { ...o, active: next } : o));
  }

  // Lists are provisioned by developers (a list is inert until a field is wired to it); admins
  // can only EDIT an existing list (rename/description) + manage its options — no create.
  const onListSaved = (row) => {
    setEditList(null);
    refreshLists();
    if (drill && drill.id === row.id) setDrill(row);
  };

  const onOptionSaved = () => {
    setAddOptionOpen(false); setEditOption(null);
    if (drill) loadOptions(drill.id);
    refreshOptionCounts();
  };

  const visibleLists = lists.filter(l => showInactive || l.active);
  const nextSort = options.reduce((mx, o) => Math.max(mx, o.sort_order || 0), 0) + 1;

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  // ── REFERENCE TABLE: Employees (structured staff CRUD) ─────────
  if (refTable === 'employees') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Employees</span>
        </div>
        <EmployeeTable darkMode={darkMode} editable />
      </div>
    );
  }

  // ── REFERENCE TABLE: Client Assignments (at-a-glance CS/TS/CM editable grid) ─────────
  if (refTable === 'assignments') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Client Assignments</span>
        </div>
        <AssignmentGrid darkMode={darkMode} />
      </div>
    );
  }

  // ── REFERENCE TABLE: Folders & Document Types (doc_folders / doc_types) ─────────
  if (refTable === 'docvocab') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Folders & Document Types</span>
        </div>
        <DocFoldersTable c={c} s={s} />
      </div>
    );
  }

  // ── REFERENCE TABLE: Notice Types (notice_types) ─────────────────────────────────
  if (refTable === 'noticetypes') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Notice Types</span>
        </div>
        <NoticeTypesTable c={c} s={s} />
      </div>
    );
  }

  // ── REFERENCE TABLE: Client Groups (client_groups / client_group_members) ─────────
  if (refTable === 'clientgroups') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Client Groups</span>
        </div>
        <ClientGroupsTable c={c} s={s} />
      </div>
    );
  }

  // ── REFERENCE TABLE: Level Names (level_names) ─────────────────────────────────
  if (refTable === 'levelnames') {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setRefTable(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / Level Names</span>
        </div>
        <LevelNamesTable c={c} s={s} />
      </div>
    );
  }

  // ── LIST DETAIL (options) ──────────────────────────────────────
  if (drill) {
    return (
      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '14px', fontSize: '13px' }}>
          <span style={s.back} onClick={() => setDrill(null)}>← Table Management</span>
          <span style={{ color: c.muted }}> / {drill.name}</span>
        </div>
        <div style={s.card}>
          <div style={s.cardH}>
            <div><div style={s.cardT}>{drill.name}{!drill.active && <span style={{ ...s.badge('248,113,113'), marginLeft: '8px' }}>Inactive</span>}</div>
              {drill.description && <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>{drill.description}</div>}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {drill.system_managed
                ? <span style={s.badge('148,163,184')}>System-managed · read-only</span>
                : <>
                    <button style={s.btnGhost} onClick={() => setEditList(drill)}>Edit List</button>
                    <button style={s.btnGhost} onClick={toggleListActive}>{drill.active ? 'Retire' : 'Restore'}</button>
                    <button style={s.btn} onClick={() => setAddOptionOpen(true)}>+ Add Option</button>
                  </>}
            </div>
          </div>
          <ScrollX style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={s.th}>Label</th><th style={s.th}>Code</th><th style={s.th}>Description</th><th style={{ ...s.th, textAlign: 'center' }}>Sort</th><th style={{ ...s.th, textAlign: 'center' }}>Active</th><th style={s.th}></th></tr></thead>
              <tbody>
                {options.length === 0
                  ? <tr><td colSpan="6" style={{ ...s.td, textAlign: 'center', color: c.muted }}>No options yet.</td></tr>
                  : options.map(o => (
                    <tr key={o.id} style={{ opacity: o.active ? 1 : 0.6 }}>
                      <td style={{ ...s.td, fontWeight: '600', color: c.text }}>{o.label}</td>
                      <td style={{ ...s.td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: c.muted }}>{o.code || '—'}</td>
                      <td style={{ ...s.td, color: c.muted }}>{o.description || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{o.sort_order ?? '—'}</td>
                      <td style={{ ...s.td, textAlign: 'center' }}>{o.active ? <span style={s.badge('52,211,153')}>Active</span> : <span style={s.badge('248,113,113')}>Retired</span>}</td>
                      <td style={s.td}>
                        {drill.system_managed
                          ? <span style={{ fontSize: '11px', color: c.muted }}>—</span>
                          : <>
                              <button style={s.btnGhost} onClick={() => setEditOption(o)}>Edit</button>
                              <button style={{ ...s.btnGhost, marginLeft: '6px' }} onClick={() => toggleOptionActive(o)}>{o.active ? 'Retire' : 'Restore'}</button>
                            </>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </ScrollX>
        </div>
        {editList && <AddListModal isDark={isDark} list={editList} onClose={() => setEditList(null)} onSuccess={onListSaved} />}
        {(addOptionOpen || editOption) && <AddOptionModal isDark={isDark} option={editOption} listId={drill.id} defaultSortOrder={nextSort} onClose={() => { setAddOptionOpen(false); setEditOption(null); }} onSuccess={onOptionSaved} />}
      </div>
    );
  }

  // ── LISTS ──────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>Table Management</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '18px' }}>Admin-editable pick lists and reference tables that power WigleyHub.</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.muted, cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
          Show inactive
        </label>
      </div>

      <div style={s.card}>
        <div style={s.cardH}>
          <div style={s.cardT}>Pick Lists</div>
          <span style={{ fontSize: '12px', color: c.muted }}>{visibleLists.length} shown</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={s.th}>Name</th><th style={s.th}>Description</th><th style={{ ...s.th, textAlign: 'center' }}>Options</th><th style={{ ...s.th, textAlign: 'center' }}>Active</th></tr></thead>
          <tbody>
            {visibleLists.length === 0
              ? <tr><td colSpan="4" style={{ ...s.td, textAlign: 'center', color: c.muted }}>No lists configured yet.</td></tr>
              : visibleLists.map(l => (
                <tr key={l.id} onClick={clickUnlessSelecting(() => loadDrill(l))} style={{ cursor: 'pointer', opacity: l.active ? 1 : 0.6 }}
                  onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>{l.name}</td>
                  <td style={{ ...s.td, color: c.muted }}>{l.description || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{optionCountByList[l.id] || 0}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{l.active ? <span style={s.badge('52,211,153')}>Active</span> : <span style={s.badge('248,113,113')}>Retired</span>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div style={s.card}>
        <div style={s.cardH}><div style={s.cardT}>Reference Tables</div></div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={s.th}>Name</th><th style={s.th}>Description</th></tr></thead>
          <tbody>
            <tr onClick={clickUnlessSelecting(() => setRefTable('employees'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Employees</td>
              <td style={{ ...s.td, color: c.muted }}>NTRG staff directory</td>
            </tr>
            <tr onClick={clickUnlessSelecting(() => setRefTable('assignments'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Client Assignments</td>
              <td style={{ ...s.td, color: c.muted }}>CS / TS / CM per client (at-a-glance, editable)</td>
            </tr>
            <tr onClick={clickUnlessSelecting(() => setRefTable('docvocab'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Folders & Document Types</td>
              <td style={{ ...s.td, color: c.muted }}>Document folders and the types filed in them (every type picker reads this)</td>
            </tr>
            <tr onClick={clickUnlessSelecting(() => setRefTable('noticetypes'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Notice Types</td>
              <td style={{ ...s.td, color: c.muted }}>Kinds of additional notice beside the original Proposed (Supplemental, Corrected, Reverse Appeal)</td>
            </tr>
            <tr onClick={clickUnlessSelecting(() => setRefTable('clientgroups'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Client Groups</td>
              <td style={{ ...s.td, color: c.muted }}>Clients reported together (e.g. Hertz Group = Hertz + DTAG)</td>
            </tr>
            <tr onClick={clickUnlessSelecting(() => setRefTable('levelnames'))} style={{ cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600', color: 'var(--red)' }}>Level Names</td>
              <td style={{ ...s.td, color: c.muted }}>What each appeal level is called per state (the Tax Savings Appeal Summary's ValueType)</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
